// Canopy logo easter-egg scenes. Plain JS, no dependencies.
// Ported from the design mockup (mockup/Header Birds.dc.html). Drawing code is
// intentionally left as-is so the result matches the mockup frame for frame.
//
// Usage:
//   const scenes = new CanopyScenes({ stage, canvas, logo, groundY: 71 });
//   logoButton.addEventListener('click', () => scenes.play());   // random scene
//   scenes.play('otters');                                        // a specific one
//   scenes.stop();                                                // on unmount
//
// stage   element the canvas covers (positioned, overflow hidden); its offsetWidth/offsetHeight size the canvas
// canvas  <canvas> absolutely positioned over the stage, width/height 100%, pointer-events none
// logo    the trees image (.product-mark); its on-screen box is read on every play, and it gets a brief
//         transform "rustle" that is cleared when the scene ends
// groundY y (px, stage coordinates) of the header's bottom line; animals walk on it

export const SCENES = ['birds', 'animals', 'night', 'otters', 'surveyor', 'solar', 'soil', 'owl'];

export class CanopyScenes {
  constructor({ stage, canvas, logo, groundY = 71, speed = 1, count = 7, respectReducedMotion = false }) {
    this.frameRef = { current: stage };
    this.canvasRef = { current: canvas };
    this.logoRef = { current: logo };
    this.groundY = groundY;
    this.respectReducedMotion = respectReducedMotion;
    this.props = { scene: 'random', speed, birdCount: count };
    this.state = {};
  }
  setState(s) { Object.assign(this.state, s); }
  play(scene = 'random') { this.props.scene = scene; this.start(); return this.state.scene; }
  stop() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current; if (cv) cv.getContext('2d').clearRect(0, 0, cv.width, cv.height);
    if (this.logoRef.current) this.logoRef.current.style.transform = '';
  }
  start() {
    const pick = this.props.scene ?? 'random';
    const all = SCENES;
    const scene = pick === 'random' ? all[Math.floor(Math.random() * all.length)] : pick;
    this.setState({ scene });
    scene === 'animals' ? this.animals() : scene === 'night' ? this.night() : scene === 'otters' ? this.otters() : scene === 'surveyor' ? this.surveyor() : scene === 'solar' ? this.solar() : scene === 'soil' ? this.soil() : scene === 'owl' ? this.owl() : this.birds();
  }
  // Added 16 Oct 2026 at Jeremy's request (not from the design handoff): two
  // scenes that stay INSIDE the header -- nothing is drawn below groundY, so
  // the speech bubbles sit above the speaker's head rather than hanging over
  // the page as the surveyor's does.
  //
  // soil: two field scientists come out of the trees. One holds up a trowel
  // of grey soil -- "That's a depleted matrix!" -- the other, with a Munsell
  // book, doubts it; they agree to look at it in the light and walk off.
  soil() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const spd = this.props.speed ?? 1, G = this.groundY;
    const ease = k => k * k * (3 - 2 * k), cl = k => Math.max(0, Math.min(1, k));
    const SRC = { x: L.x + .6 * L.w, y: L.y + .8 * L.h };
    const OUT = 1900, WALK0 = 12400, WALK = 3600, AWAY = 230, END = WALK0 + WALK;
    // [0] says it is depleted and stands on the right; [1] doubts it, on the left.
    const crew = [
      { delay: 0, to: L.x + L.w + 66, look: -1, kit: 'trowel', shirt: '#4f6b8a', shirtDk: '#3d5470', hat: '#f2ad3a', hatDk: '#d68f1e', hair: '#5a3a22', skin: '#d6a479' },
      { delay: 650, to: L.x + L.w + 26, look: 1, kit: 'munsell', shirt: '#7a5a8c', shirtDk: '#5f466e', hat: '#eceae2', hatDk: '#9e968a', hair: '#c79a55', skin: '#e2b48c' }
    ];
    const LINES = [
      { who: 0, t0: 2900, t1: 5500, text: "That's a depleted matrix!" },
      { who: 1, t0: 5800, t1: 9300, text: 'Are you sure? It looks like natural soil color.' },
      { who: 0, t0: 9600, t1: 12100, text: "Let's go look at it in the light." }
    ];
    const limb = (x, y, a, l, w, col) => { const ex = x + Math.sin(a) * l, ey = y + Math.cos(a) * l; ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, ey); ctx.stroke(); return [ex, ey]; };
    const kit = (o, h) => {
      if (o.kit === 'trowel') {
        ctx.fillStyle = '#8b5a2b'; ctx.fillRect(h[0] - .6, h[1] - .5, 2.4, 1);
        ctx.fillStyle = '#9aa0a6'; ctx.beginPath(); ctx.moveTo(h[0] + 1.8, h[1] - 1.3); ctx.lineTo(h[0] + 5.6, h[1]); ctx.lineTo(h[0] + 1.8, h[1] + 1.3); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#8d8b80'; ctx.beginPath(); ctx.ellipse(h[0] + 3.3, h[1] - 1, 1.7, 1, 0, 0, 6.29); ctx.fill();
        ctx.fillStyle = '#c0652a'; ctx.beginPath(); ctx.arc(h[0] + 3.8, h[1] - 1.2, .38, 0, 6.29); ctx.fill();
      } else {
        ctx.fillStyle = '#b5541f'; ctx.fillRect(h[0] - .3, h[1] - 2.8, 3.2, 3.9);
        ctx.fillStyle = '#f7f6f1'; ctx.fillRect(h[0] + 2.5, h[1] - 2.5, .5, 3.3);
      }
    };
    const person = (p, walking, raise, o) => {
      const sw = walking ? Math.sin(p) * .5 : 0, bob = walking ? -Math.abs(Math.cos(p)) * .7 : 0;
      const pants = '#6b6250', pantsDk = '#4f4839', boot = '#4a3a28';
      ctx.translate(0, bob);
      let f = limb(-.5, -10, -sw, 9.4, 2.5, pantsDk); ctx.fillStyle = boot; ctx.beginPath(); ctx.ellipse(f[0] + .9, f[1] - .3, 1.9, 1, 0, 0, 6.29); ctx.fill();
      limb(-.6, -17.4, walking ? sw * .6 + .15 : .2, 6.2, 1.6, o.shirtDk);
      f = limb(.5, -10, sw, 9.4, 2.7, pants); ctx.fillStyle = boot; ctx.beginPath(); ctx.ellipse(f[0] + .9, f[1] - .3, 1.9, 1, 0, 0, 6.29); ctx.fill();
      ctx.fillStyle = pants; ctx.beginPath(); ctx.moveTo(-2.6, -13); ctx.lineTo(2.6, -13); ctx.lineTo(2.9, -9.6); ctx.lineTo(-2.8, -9.6); ctx.closePath(); ctx.fill();
      ctx.fillStyle = o.shirt; ctx.beginPath(); ctx.moveTo(-2.3, -18.6); ctx.lineTo(2.3, -18.6); ctx.lineTo(2.8, -12.8); ctx.lineTo(-2.8, -12.8); ctx.closePath(); ctx.fill();
      // Ponytail first, so the head covers its root; it swings a little on the walk.
      const swing = walking ? Math.sin(p * 2) * .45 : 0;
      ctx.fillStyle = o.hair; ctx.beginPath(); ctx.moveTo(-1.2, -22.3); ctx.quadraticCurveTo(-4.9 + swing, -21.4, -4.4 + swing, -17.4); ctx.quadraticCurveTo(-3.1, -19.4, -1.3, -20); ctx.closePath(); ctx.fill();
      ctx.fillStyle = o.skin; ctx.beginPath(); ctx.arc(.5, -20.8, 2.25, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(2.7, -20.6, .55, 0, 6.29); ctx.fill();
      ctx.fillStyle = o.hair; ctx.beginPath(); ctx.arc(.5, -20.8, 2.3, Math.PI * .62, Math.PI * 1.5); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#1f2414'; ctx.beginPath(); ctx.arc(1.6, -21.1, .32, 0, 6.29); ctx.fill();
      ctx.fillStyle = o.hat; ctx.beginPath(); ctx.arc(.3, -21.7, 2.5, Math.PI, 0); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.ellipse(.7, -21.6, 3.8, .65, 0, 0, 6.29); ctx.fill();
      ctx.fillStyle = o.hatDk; ctx.fillRect(-2.1, -22.5, 4.8, .55);
      const h = limb(.8, -17.4, raise ? 2.05 : walking ? -sw * .6 - .1 : -.15, raise ? 5.8 : 6.4, 1.7, o.shirt);
      ctx.fillStyle = o.skin; ctx.beginPath(); ctx.arc(h[0], h[1], .85, 0, 6.29); ctx.fill();
      kit(o, h);
    };
    // Above the speaker's head and inside the header: the tail points down at
    // the head, the box is kept on screen at either edge.
    const bubble = (sx, text, a, k) => {
      ctx.save(); ctx.font = "500 12px 'IBM Plex Sans', sans-serif";
      const tw = ctx.measureText(text).width, bw = tw + 18, bh = 22, tipY = G - 28;
      const bx = Math.max(6, Math.min(W - bw - 6, sx - 16)), by = tipY - 7 - bh;
      const tx = Math.max(bx + 8, Math.min(bx + bw - 8, sx));
      ctx.globalAlpha = a; ctx.translate(sx, tipY); ctx.scale(k, k); ctx.translate(-sx, -tipY);
      ctx.shadowColor = 'rgba(31,36,20,.14)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 2;
      ctx.fillStyle = '#fff'; ctx.strokeStyle = '#9e968a'; ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(bx + 4, by); ctx.lineTo(bx + bw - 4, by); ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + 4);
      ctx.lineTo(bx + bw, by + bh - 4); ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - 4, by + bh);
      ctx.lineTo(tx + 4, by + bh); ctx.lineTo(sx, tipY); ctx.lineTo(tx - 3, by + bh);
      ctx.lineTo(bx + 4, by + bh); ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - 4); ctx.lineTo(bx, by + 4); ctx.quadraticCurveTo(bx, by, bx + 4, by);
      ctx.closePath(); ctx.fill(); ctx.shadowColor = 'transparent'; ctx.stroke();
      ctx.fillStyle = '#1f2414'; ctx.textBaseline = 'middle'; ctx.fillText(text, bx + 9, by + bh / 2 + .5);
      ctx.restore();
    };
    const rustles = [], t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      const at = [];
      crew.forEach((o, i) => {
        const lt = el - o.delay; if (lt < 0) return;
        let x, y = G, s = 1, dir = 1, walking = false, raise = false, alpha = 1;
        if (lt < OUT) {
          const k = lt / OUT, e = ease(k);
          x = SRC.x + (o.to - SRC.x) * e; y = SRC.y + (G - SRC.y) * e - Math.sin(k * Math.PI) * 4; s = .42 + .58 * e; walking = true; alpha = Math.min(1, k / .12);
          if (!o.out) { o.out = true; rustles.push(now); }
        } else if (el < WALK0) {
          x = o.to; dir = lt < OUT + 200 ? 1 : o.look;
          raise = LINES.some(l => l.who === i && el > l.t0 - 150 && el < l.t1);
        } else {
          const k = cl((el - WALK0) / WALK);
          x = o.to + AWAY * k; walking = true; alpha = k > .65 ? 1 - (k - .65) / .35 : 1;
        }
        at[i] = x;
        if (alpha <= 0) return;
        ctx.save(); ctx.globalAlpha = alpha; ctx.translate(x, y); ctx.scale(dir * s, s);
        person(el / 1000 * 2.2 * 6.283 + i * 1.7, walking, raise, o); ctx.restore();
      });
      for (const l of LINES) {
        if (el < l.t0 || el > l.t1 + 200 || at[l.who] === undefined) continue;
        const a = el < l.t0 + 180 ? (el - l.t0) / 180 : el > l.t1 ? 1 - (el - l.t1) / 200 : 1;
        const k = el < l.t0 + 180 ? .85 + .15 * ease((el - l.t0) / 180) : 1;
        bubble(at[l.who] + (crew[l.who].look > 0 ? 2 : -2), l.text, Math.max(0, a), k);
      }
      let rot = 0, sy = 1;
      for (const r0 of rustles) { const d = (now - r0) / 1000; if (d < .7) { const e = Math.exp(-d * 6); rot += Math.sin(d * 34) * .7 * e; sy += Math.sin(d * 28) * .01 * e; } }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (el < END || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(frame);
  }
  // owl: a squirrel runs in along the header's bottom line and sits to eat an
  // acorn; an owl leaves the trees and swoops, the squirrel bolts a moment
  // before the talons land, and the owl flies back to the trees empty-footed.
  owl() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const spd = this.props.speed ?? 1, G = this.groundY;
    const ease = k => k * k * (3 - 2 * k), cl = k => Math.max(0, Math.min(1, k));
    const cr = (p0, p1, p2, p3, t) => { const t2 = t * t, t3 = t2 * t; return .5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3); };
    const along = (pts, u) => { const segs = pts.length - 3, f = Math.min(u * segs, segs - 1e-6), k = Math.floor(f), t = f - k; const a = pts[k], b = pts[k + 1], c = pts[k + 2], d = pts[k + 3]; return [cr(a[0], b[0], c[0], d[0], t), cr(a[1], b[1], c[1], d[1], t)]; };
    const perch = { x: L.x + .55 * L.w, y: L.y + .35 * L.h };
    const SQ = Math.min(W - 160, L.x + L.w + 300);
    const IN = 2200, ALERT = 3780, BOLT = 3950, GONE = 5600;
    const LEAVE = 2300, STRIKE = 4300, LIFT = 4900, HOME = 7100;
    const dive = [[perch.x - 10, perch.y + 6], [perch.x, perch.y], [perch.x + 70, 20], [SQ - 100, 19], [SQ - 30, G - 30], [SQ + 1, G - 7], [SQ + 6, G - 4]];
    const back = [[SQ + 1, G - 7], [SQ + 1, G - 25], [SQ - 120, 20], [perch.x + 50, perch.y - 6], [perch.x, perch.y], [perch.x - 10, perch.y + 5]];
    const owl = (f, talons) => {
      const brown = '#6e5434', dk = '#4a3822', belly = '#d9c3a0', disc = '#e8d9bc';
      ctx.fillStyle = dk; ctx.beginPath(); ctx.moveTo(.6, -3.6); ctx.quadraticCurveTo(-3.4, -5 - f * 4.4, -9.2, -3.4 - f * 7); ctx.quadraticCurveTo(-6, -f * 2.6 + 1, -.6, .2); ctx.closePath(); ctx.fill();
      ctx.fillStyle = brown; ctx.beginPath(); ctx.moveTo(-4, .8); ctx.lineTo(-8, 2.6); ctx.lineTo(-4, 3.6); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.ellipse(0, -1, 4.2, 5.4, .25, 0, 6.29); ctx.fill();
      ctx.fillStyle = belly; ctx.beginPath(); ctx.ellipse(1.3, -.2, 2.3, 3.8, .25, 0, 6.29); ctx.fill();
      ctx.fillStyle = brown; for (const [bx, by] of [[1.1, -1.4], [2, .4], [.8, 1.6]]) { ctx.beginPath(); ctx.arc(bx, by, .35, 0, 6.29); ctx.fill(); }
      ctx.beginPath(); ctx.arc(2.2, -6.4, 3.4, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.moveTo(.4, -8.8); ctx.lineTo(-.2, -11.2); ctx.lineTo(1.7, -9.5); ctx.closePath(); ctx.moveTo(3, -9.6); ctx.lineTo(3.6, -11.8); ctx.lineTo(4.5, -9.2); ctx.closePath(); ctx.fill();
      ctx.fillStyle = disc; ctx.beginPath(); ctx.arc(3.3, -6.2, 2.3, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#f2c230'; ctx.beginPath(); ctx.arc(3.9, -6.7, 1.05, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#1f1a12'; ctx.beginPath(); ctx.arc(4.2, -6.7, .52, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#c9a24a'; ctx.beginPath(); ctx.moveTo(5.3, -6.2); ctx.lineTo(6.5, -5.4); ctx.lineTo(5.2, -4.9); ctx.closePath(); ctx.fill();
      if (talons) {
        ctx.strokeStyle = '#c9a24a'; ctx.lineWidth = .8; ctx.lineCap = 'round'; ctx.beginPath();
        ctx.moveTo(1.4, 3.6); ctx.lineTo(2.8, 6.8); ctx.lineTo(4, 7); ctx.moveTo(.2, 3.8); ctx.lineTo(1, 7.2); ctx.lineTo(2.2, 7.5); ctx.stroke();
      }
      ctx.fillStyle = brown; ctx.beginPath(); ctx.moveTo(.8, -3.4); ctx.quadraticCurveTo(-3, -4.4 - f * 5, -10.4, -2.4 - f * 8); ctx.quadraticCurveTo(-6.4, -f * 3 + 1.8, -.4, .8); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = belly; ctx.lineWidth = .4; ctx.beginPath(); ctx.moveTo(-3, -3.2 - f * 3); ctx.lineTo(-6.6, -1.6 - f * 5.2); ctx.stroke();
    };
    const squirrel = (p, mode) => {
      const c = '#8a7f72', dk = '#5d554b', belly = '#e6dfd2';
      if (mode === 'sit' || mode === 'alert') {
        const nib = mode === 'sit' ? Math.sin(p * 3) * .3 : 0;
        ctx.fillStyle = c; ctx.beginPath(); ctx.moveTo(-2, -1); ctx.bezierCurveTo(-9, -2, -9.4, -13, -4.2, -14.2); ctx.bezierCurveTo(-1.2, -14.8, -1.4, -11.2, -3.6, -11); ctx.bezierCurveTo(-6.6, -10.6, -6.2, -4, -1, -3); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.ellipse(0, -4.6, 2.9, 4.4, .1, 0, 6.29); ctx.fill();
        ctx.beginPath(); ctx.ellipse(-.6, -1.4, 2.6, 1.7, 0, 0, 6.29); ctx.fill();
        ctx.fillStyle = dk; ctx.beginPath(); ctx.ellipse(1.3, -.3, 1.9, .6, 0, 0, 6.29); ctx.fill();
        ctx.fillStyle = belly; ctx.beginPath(); ctx.ellipse(1.2, -4.2, 1.4, 3, .1, 0, 6.29); ctx.fill();
        ctx.fillStyle = c; ctx.beginPath(); ctx.arc(1.3, -9.6 + nib, 2.3, 0, 6.29); ctx.fill();
        ctx.beginPath(); ctx.moveTo(.3, -11.2 + nib); ctx.lineTo(mode === 'alert' ? .3 : -.2, mode === 'alert' ? -14.2 : -13.2); ctx.lineTo(1.6, -11.6 + nib); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#1f1a12'; ctx.beginPath(); ctx.arc(2.3, -10 + nib, .42, 0, 6.29); ctx.fill();
        ctx.beginPath(); ctx.arc(3.5, -9.3 + nib, .32, 0, 6.29); ctx.fill();
        if (mode === 'sit') {
          ctx.fillStyle = '#9a6a2e'; ctx.beginPath(); ctx.ellipse(3.3, -7.6 + nib, .9, 1.1, 0, 0, 6.29); ctx.fill();
          ctx.fillStyle = '#5a3e1e'; ctx.fillRect(2.4, -8.8 + nib, 1.8, .55);
        }
        ctx.fillStyle = dk; ctx.beginPath(); ctx.arc(2.6, -6.8, .6, 0, 6.29); ctx.fill();
        return;
      }
      const leap = mode === 'leap', g = leap ? .8 : Math.sin(p), hop = leap ? 0 : Math.max(0, Math.sin(p)) * 1.6;
      ctx.translate(0, -hop);
      ctx.strokeStyle = dk; ctx.lineWidth = 1.2; ctx.lineCap = 'round'; ctx.beginPath();
      ctx.moveTo(2.4, -2.2); ctx.lineTo(2.4 + Math.sin(g * .9) * 2.6, -2.2 + Math.cos(g * .9) * 2.4);
      ctx.moveTo(-2.4, -2.2); ctx.lineTo(-2.4 - Math.sin(g * .9) * 2.6, -2.2 + Math.cos(g * .9) * 2.4); ctx.stroke();
      ctx.fillStyle = c; ctx.beginPath(); ctx.moveTo(-3.6, -3.6); ctx.bezierCurveTo(-9, -4.6 - g, -12.4, -9.4 + g * .8, -8.4, -11); ctx.bezierCurveTo(-6.4, -11.6, -5.6, -9.6, -7, -8.8); ctx.bezierCurveTo(-8.4, -7.6, -6.4, -5.6, -3.2, -5.4); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.ellipse(0, -3.6, 4.6, 2.3, -.08 + g * .06, 0, 6.29); ctx.fill();
      ctx.fillStyle = belly; ctx.beginPath(); ctx.ellipse(.6, -2.6, 2.8, .9, 0, 0, 6.29); ctx.fill();
      ctx.fillStyle = c; ctx.beginPath(); ctx.arc(4.4, -4.8, 2, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.moveTo(3.6, -6.4); ctx.lineTo(3.6, -8.6); ctx.lineTo(4.8, -6.6); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#1f1a12'; ctx.beginPath(); ctx.arc(5.2, -5.1, .38, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(6.3, -4.6, .3, 0, 6.29); ctx.fill();
    };
    const rustles = [], t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      const endFade = el > HOME - 500 ? cl((HOME + 300 - el) / 800) : 1;
      // The acorn it was eating stays where it dropped until the end.
      if (el > BOLT) { ctx.globalAlpha = endFade; ctx.fillStyle = '#9a6a2e'; ctx.beginPath(); ctx.ellipse(SQ + 3.4, G - 1.1, .95, 1.1, 0, 0, 6.29); ctx.fill(); ctx.fillStyle = '#5a3e1e'; ctx.fillRect(SQ + 2.5, G - 2.6, 1.8, .55); ctx.globalAlpha = 1; }
      // The squirrel.
      if (el < GONE) {
        let x, y = G, dir = -1, mode = 'run', rot = 0;
        if (el < IN) { x = W + 20 + (SQ - W - 20) * (el / IN); }
        else if (el < ALERT) { x = SQ; dir = 1; mode = 'sit'; }
        else if (el < BOLT) { x = SQ; dir = 1; mode = 'alert'; }
        else {
          const lt = el - BOLT; dir = 1;
          if (lt < 320) { const k = lt / 320; x = SQ + 30 * k; y = G - Math.sin(k * Math.PI) * 11; mode = 'leap'; rot = -.35 * Math.cos(k * Math.PI); }
          else { x = SQ + 30 + (W + 30 - SQ - 30) * ((lt - 320) / (GONE - BOLT - 320)); }
        }
        ctx.save(); ctx.translate(x, y); ctx.scale(dir * 1.1, 1.1); ctx.rotate(rot);
        squirrel(el / 1000 * 6 * 6.283, mode); ctx.restore();
      }
      // The owl.
      if (el > LEAVE && el < HOME) {
        let x, y, s = 1, dir = 1, f, talons = false, rot = 0, alpha = 1;
        if (el < STRIKE) {
          const k = (el - LEAVE) / (STRIKE - LEAVE), u = k < .7 ? k * .78 / .7 : .78 + ease((k - .7) / .3) * .22;
          [x, y] = along(dive, u); s = .45 + .55 * cl(k / .3); alpha = Math.min(1, k / .06);
          f = k > .78 ? -.7 : Math.sin(el / 1000 * 5 * 6.283); talons = k > .8; rot = k > .7 ? .3 : .05;
          if (!rustles.length) rustles.push(now);
        } else if (el < LIFT) {
          const k = (el - STRIKE) / (LIFT - STRIKE);
          x = SQ + 1; y = G - 7 - ease(k) * 18; f = Math.sin(el / 1000 * 7 * 6.283); talons = true; rot = -.1;
        } else {
          const k = (el - LIFT) / (HOME - LIFT);
          [x, y] = along(back, k); dir = -1; s = k > .7 ? 1 - .55 * (k - .7) / .3 : 1; alpha = k > .85 ? 1 - (k - .85) / .15 : 1;
          f = Math.sin(el / 1000 * 5 * 6.283);
          if (k > .97 && rustles.length < 2) rustles.push(now);
        }
        ctx.save(); ctx.globalAlpha = alpha; ctx.translate(x, y); ctx.scale(dir * s, s); ctx.rotate(rot);
        owl(f, talons); ctx.restore();
      }
      // A puff of dust where the talons came down on nothing.
      if (el > STRIKE && el < STRIKE + 600) {
        const a = (el - STRIKE) / 600;
        ctx.fillStyle = `rgba(158,150,138,${(.55 * (1 - a)).toFixed(3)})`;
        for (const dx of [-5, 0, 5]) { ctx.beginPath(); ctx.arc(SQ + 1 + dx * (1 + a), G - 1.5 - a * 3, 1.4 + a * 2.2, 0, 6.29); ctx.fill(); }
      }
      let rot = 0, sy = 1;
      for (const r0 of rustles) { const d = (now - r0) / 1000; if (d < .7) { const e = Math.exp(-d * 6); rot += Math.sin(d * 34) * .7 * e; sy += Math.sin(d * 28) * .01 * e; } }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (el < HOME + 300 || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(frame);
  }
  solar() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const spd = this.props.speed ?? 1, G = this.groundY, R = (a, b) => a + Math.random() * (b - a);
    const ease = k => k * k * (3 - 2 * k), cl = k => Math.max(0, Math.min(1, k));
    const A = { x: L.x + .6 * L.w, y: L.y + .8 * L.h }, B = { x: L.x + L.w + 16, y: G };
    const OUT = 1800, END_X = W - 70, WALK = 8200, WALK1 = OUT + WALK, HOLD1 = WALK1 + 4600, FADE = 1300;
    const xAt = (t) => t < OUT ? null : B.x + (END_X - B.x) * cl((t - OUT) / WALK);
    const tAt = (x) => OUT + (x - B.x) / (END_X - B.x) * WALK;
    const panels = [];
    for (let x = B.x + 44; x < END_X - 20; x += 46) panels.push({ x, t: tAt(x + 14) });
    const g0 = panels[0].x - 24, g1 = panels[panels.length - 1].x + 24;
    const blades = [], flowers = [];
    for (let x = g0; x < g1; x += R(1.2, 2.4)) blades.push({ x, h: R(3, 7.5), lean: R(-1.6, 1.6), col: Math.random() < .55 ? '#8cc63f' : '#6f8a2a', t: tAt(x) + 900 + R(0, 600) });
    const fcols = ['#f6ec6e', '#ffffff', '#c9a0dc', '#f6ec6e', '#f4ae3a'];
    for (let x = g0 + 4; x < g1; x += R(7, 15)) flowers.push({ x, h: R(6, 10), col: fcols[Math.floor(Math.random() * fcols.length)], t: tAt(x) + 1900 + R(0, 900), r: R(1.1, 1.6) });
    const limb = (x, y, a, l, w, col) => { const ex = x + Math.sin(a) * l, ey = y + Math.cos(a) * l; ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, ey); ctx.stroke(); return [ex, ey]; };
    const person = (p, walking, pose) => {
      const sw = walking ? Math.sin(p) * .5 : 0, bob = walking ? -Math.abs(Math.cos(p)) * .7 : 0;
      const pants = '#3d4a5c', pantsDk = '#2c3645', boot = '#4a3a28', vest = '#cfdc2b', stripe = '#eceae2', shirt = '#4f5e10', skin = '#d6a479', hat = '#ffffff', tool = '#3a3a38';
      ctx.translate(0, bob);
      let f = limb(-.5, -10, -sw, 9.4, 2.5, pantsDk); ctx.fillStyle = boot; ctx.beginPath(); ctx.ellipse(f[0] + .9, f[1] - .3, 1.9, 1, 0, 0, 6.29); ctx.fill();
      const fh = limb(-.6, -17.4, walking ? sw * .6 + .15 : pose === 'hips' ? -.9 : .2, pose === 'hips' ? 4 : 6.4, 1.6, shirt);
      if (pose !== 'hips') { ctx.fillStyle = tool; ctx.fillRect(fh[0] - .6, fh[1] - .4, 3.2, 1.6); ctx.fillRect(fh[0] - .4, fh[1], 1.2, 2.4); ctx.fillStyle = '#f4ae3a'; ctx.fillRect(fh[0] - .4, fh[1] + 1.6, 1.2, .9); }
      f = limb(.5, -10, sw, 9.4, 2.7, pants); ctx.fillStyle = boot; ctx.beginPath(); ctx.ellipse(f[0] + .9, f[1] - .3, 1.9, 1, 0, 0, 6.29); ctx.fill();
      ctx.fillStyle = pants; ctx.beginPath(); ctx.moveTo(-2.5, -13); ctx.lineTo(2.6, -13); ctx.lineTo(2.9, -9.6); ctx.lineTo(-2.7, -9.6); ctx.closePath(); ctx.fill();
      ctx.fillStyle = shirt; ctx.fillRect(-2.6, -18.6, 5.2, 5.8);
      ctx.fillStyle = vest; ctx.beginPath(); ctx.moveTo(-2.9, -18.4); ctx.lineTo(-.6, -18.6); ctx.lineTo(.4, -16.4); ctx.lineTo(1.4, -18.6); ctx.lineTo(3, -18.4); ctx.lineTo(3.1, -12.6); ctx.lineTo(-3, -12.6); ctx.closePath(); ctx.fill();
      ctx.fillStyle = stripe; ctx.fillRect(-3, -14.6, 6.1, .9);
      ctx.fillStyle = skin; ctx.beginPath(); ctx.arc(.5, -20.8, 2.25, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(2.7, -20.6, .6, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#1f2414'; ctx.beginPath(); ctx.arc(1.6, -21.1, .32, 0, 6.29); ctx.fill();
      ctx.fillStyle = hat; ctx.strokeStyle = '#9e968a'; ctx.lineWidth = .35;
      ctx.beginPath(); ctx.arc(.3, -21.7, 2.5, Math.PI, 0); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.ellipse(.7, -21.6, 3.8, .65, 0, 0, 6.29); ctx.fill(); ctx.stroke();
      if (pose === 'hips') { limb(.8, -17.4, .9, 4, 1.7, shirt); }
      else { const h = limb(.8, -17.4, walking ? -sw * .6 - .1 : -.15, 6.4, 1.7, shirt); ctx.fillStyle = skin; ctx.beginPath(); ctx.arc(h[0], h[1], .85, 0, 6.29); ctx.fill(); }
    };
    const panel = (x, k, sheen) => {
      const post = ease(cl(k / .35)), flip = ease(cl((k - .3) / .7));
      ctx.strokeStyle = '#7d776c'; ctx.lineWidth = 1.4; ctx.lineCap = 'butt';
      const ph = 9 * post;
      ctx.beginPath(); ctx.moveTo(x - 11, G); ctx.lineTo(x - 11, G - ph * .8); ctx.moveTo(x + 11, G); ctx.lineTo(x + 11, G - ph); ctx.stroke();
      if (flip <= 0) return;
      const cy = G - 9 - 2.5, tilt = .42 * flip, hw = 19, hh = 5.2 * flip + .4;
      ctx.save(); ctx.translate(x, cy);
      ctx.transform(1, -tilt * .35, 0, 1, 0, 0);
      ctx.fillStyle = '#24394f'; ctx.fillRect(-hw, -hh, hw * 2, hh * 2);
      ctx.strokeStyle = '#5d7a96'; ctx.lineWidth = .4; ctx.beginPath();
      for (let i = 1; i < 6; i++) { const gx = -hw + i * hw * 2 / 6; ctx.moveTo(gx, -hh); ctx.lineTo(gx, hh); }
      ctx.moveTo(-hw, 0); ctx.lineTo(hw, 0); ctx.stroke();
      ctx.strokeStyle = '#c9c3b4'; ctx.lineWidth = .7; ctx.strokeRect(-hw, -hh, hw * 2, hh * 2);
      if (sheen > -1) {
        const sx = -hw - 10 + sheen * (hw * 2 + 20);
        const gr = ctx.createLinearGradient(sx - 6, 0, sx + 6, 0);
        gr.addColorStop(0, 'rgba(255,255,255,0)'); gr.addColorStop(.5, 'rgba(255,255,255,.55)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = gr; ctx.fillRect(-hw, -hh, hw * 2, hh * 2);
      }
      ctx.restore();
    };
    const rustles = [], t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      const fade = el > HOLD1 ? 1 - cl((el - HOLD1) / FADE) : 1;
      ctx.globalAlpha = fade;
      for (const b of blades) {
        const k = ease(cl((el - b.t) / 1100)); if (k <= 0) continue;
        const h = b.h * k, sway = Math.sin(el / 700 + b.x * .3) * .5 * k;
        ctx.strokeStyle = b.col; ctx.lineWidth = .8; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(b.x, G); ctx.quadraticCurveTo(b.x + b.lean * .3, G - h * .6, b.x + b.lean + sway, G - h); ctx.stroke();
      }
      for (const p of panels) {
        const k = cl((el - p.t) / 650); if (k <= 0) continue;
        const sh = el > WALK1 + 500 ? ((el - WALK1 - 500) / 1600 - (p.x - panels[0].x) / (g1 - g0) * .9) : -2;
        panel(p.x, k, sh >= 0 && sh <= 1 ? sh : -2);
      }
      for (const f of flowers) {
        const k = ease(cl((el - f.t) / 900)); if (k <= 0) continue;
        const h = f.h * k, sway = Math.sin(el / 650 + f.x) * .6 * k, tx = f.x + sway, ty = G - h;
        ctx.strokeStyle = '#6f8a2a'; ctx.lineWidth = .6; ctx.beginPath(); ctx.moveTo(f.x, G); ctx.quadraticCurveTo(f.x, G - h * .5, tx, ty); ctx.stroke();
        const bloom = cl((k - .6) / .4);
        if (bloom > 0) {
          ctx.fillStyle = f.col; const r = f.r * bloom;
          for (let i = 0; i < 5; i++) { const a = i * 1.2566 + f.x; ctx.beginPath(); ctx.arc(tx + Math.cos(a) * r * .8, ty + Math.sin(a) * r * .8, r * .62, 0, 6.29); ctx.fill(); }
          ctx.fillStyle = f.col === '#f6ec6e' || f.col === '#f4ae3a' ? '#7a4a1a' : '#e3b51f'; ctx.beginPath(); ctx.arc(tx, ty, r * .45, 0, 6.29); ctx.fill();
        }
      }
      let x, y, s = 1, dir = 1, walking = true, pose = '', alpha = 1;
      if (el < OUT) { const k = el / OUT, e = ease(k); x = A.x + (B.x - A.x) * e; y = A.y + (B.y - A.y) * e - Math.sin(k * Math.PI) * 4; s = .42 + .58 * e; alpha = Math.min(1, k / .12); if (!rustles.length) rustles.push(now); }
      else if (el < WALK1) { x = xAt(el); y = G; }
      else { x = END_X; y = G; walking = false; dir = el < WALK1 + 300 ? 1 : -1; pose = el > WALK1 + 600 ? 'hips' : ''; }
      ctx.save(); ctx.globalAlpha = alpha * fade; ctx.translate(x, y); ctx.scale(dir * s, s);
      person(el / 1000 * 2.4 * 6.283, walking, pose); ctx.restore();
      ctx.globalAlpha = 1;
      let rot = 0, sy = 1;
      for (const r0 of rustles) { const d = (now - r0) / 1000; if (d < .7) { const e = Math.exp(-d * 6); rot += Math.sin(d * 34) * .7 * e; sy += Math.sin(d * 28) * .01 * e; } }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (el < HOLD1 + FADE || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(frame);
  }
  surveyor() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const spd = this.props.speed ?? 1;
    const A = { x: L.x + .6 * L.w, y: L.y + .8 * L.h, s: .42 }, B = { x: L.x + L.w + 16, y: this.groundY, s: 1 };
    const OUT = 1900, Q0 = 2150, Q1 = 5400, BACK0 = 5650, BACK1 = 7550;
    const limb = (x, y, a, l, w, col) => { const ex = x + Math.sin(a) * l, ey = y + Math.cos(a) * l; ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, ey); ctx.stroke(); return [ex, ey]; };
    const person = (p, walking, point) => {
      const sw = walking ? Math.sin(p) * .5 : 0, bob = walking ? -Math.abs(Math.cos(p)) * .7 : 0;
      const wader = '#3f4c1d', waderDk = '#2a3313', boot = '#1f2414', vest = '#cfdc2b', stripe = '#eceae2', shirt = '#a99472', skin = '#d6a479', hat = '#f2ad3a', flag = '#ff4f9a';
      ctx.translate(0, bob);
      let f = limb(-.5, -10, -sw, 9.4, 2.5, waderDk); ctx.fillStyle = boot; ctx.beginPath(); ctx.ellipse(f[0] + .9, f[1] - .3, 1.9, 1, 0, 0, 6.29); ctx.fill();
      const fh = limb(-.6, -17.4, walking ? sw * .6 + .15 : .25, 6.4, 1.6, shirt);
      ctx.strokeStyle = '#6b6658'; ctx.lineWidth = .5; ctx.beginPath(); ctx.moveTo(fh[0], fh[1] + 1.5); ctx.lineTo(fh[0] + .3, fh[1] - 9); ctx.stroke();
      ctx.fillStyle = flag; ctx.beginPath(); ctx.moveTo(fh[0] + .3, fh[1] - 9); ctx.lineTo(fh[0] + 4, fh[1] - 8.2); ctx.lineTo(fh[0] + .3, fh[1] - 6.6); ctx.closePath(); ctx.fill();
      f = limb(.5, -10, sw, 9.4, 2.7, wader); ctx.fillStyle = boot; ctx.beginPath(); ctx.ellipse(f[0] + .9, f[1] - .3, 1.9, 1, 0, 0, 6.29); ctx.fill();
      ctx.fillStyle = wader; ctx.beginPath(); ctx.moveTo(-2.4, -15); ctx.lineTo(2.6, -15); ctx.lineTo(2.9, -9.6); ctx.lineTo(-2.7, -9.6); ctx.closePath(); ctx.fill();
      ctx.fillStyle = shirt; ctx.beginPath(); ctx.moveTo(-2.6, -18.6); ctx.lineTo(2.6, -18.6); ctx.lineTo(2.6, -15); ctx.lineTo(-2.6, -15); ctx.closePath(); ctx.fill();
      ctx.fillStyle = vest; ctx.beginPath(); ctx.moveTo(-2.9, -18.4); ctx.lineTo(-.6, -18.6); ctx.lineTo(.4, -16.4); ctx.lineTo(1.4, -18.6); ctx.lineTo(3, -18.4); ctx.lineTo(3.1, -12.6); ctx.lineTo(-3, -12.6); ctx.closePath(); ctx.fill();
      ctx.fillStyle = stripe; ctx.fillRect(-3, -14.6, 6.1, .9);
      ctx.fillStyle = '#1f2414'; ctx.fillRect(1.2, -17.4, 1.3, 1.6);
      ctx.fillStyle = '#e9e6dc'; ctx.fillRect(1.35, -17.25, 1, 1.2);
      ctx.fillStyle = skin; ctx.beginPath(); ctx.arc(.5, -20.8, 2.25, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(2.7, -20.6, .6, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#1f2414'; ctx.beginPath(); ctx.arc(1.6, -21.1, .32, 0, 6.29); ctx.fill();
      ctx.fillStyle = hat; ctx.beginPath(); ctx.arc(.3, -21.7, 2.5, Math.PI, 0); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.ellipse(.7, -21.6, 3.8, .65, 0, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#d68f1e'; ctx.fillRect(-.1, -24.1, .8, 2.4);
      if (point) {
        ctx.save(); ctx.strokeStyle = shirt; ctx.lineWidth = 1.7; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(.8, -17.4); ctx.lineTo(4.4, -17.2 + point * 0); ctx.lineTo(7.4, -18.4); ctx.stroke();
        ctx.fillStyle = skin; ctx.beginPath(); ctx.arc(7.7, -18.5, .85, 0, 6.29); ctx.fill(); ctx.restore();
      } else {
        const h = limb(.8, -17.4, walking ? -sw * .6 - .1 : -.15, 6.4, 1.7, shirt);
        ctx.fillStyle = skin; ctx.beginPath(); ctx.arc(h[0], h[1], .85, 0, 6.29); ctx.fill();
      }
    };
    const TEXT = 'Is that water jurisdictional?';
    const bubble = (x, y, a, k) => {
      ctx.save(); ctx.font = "500 13px 'IBM Plex Sans', sans-serif";
      const tw = ctx.measureText(TEXT).width, bw = tw + 22, bh = 28, bx = x + 4, by = this.groundY + 9;
      ctx.globalAlpha = a; ctx.translate(bx, by); ctx.scale(k, k);
      ctx.shadowColor = 'rgba(31,36,20,.14)'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 4;
      ctx.fillStyle = '#fff'; ctx.strokeStyle = '#9e968a'; ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(4, 0); ctx.lineTo(10, 0); ctx.lineTo(4, -13); ctx.lineTo(20, 0); ctx.lineTo(bw - 4, 0); ctx.quadraticCurveTo(bw, 0, bw, 4);
      ctx.lineTo(bw, bh - 4); ctx.quadraticCurveTo(bw, bh, bw - 4, bh); ctx.lineTo(4, bh); ctx.quadraticCurveTo(0, bh, 0, bh - 4); ctx.lineTo(0, 4); ctx.quadraticCurveTo(0, 0, 4, 0);
      ctx.closePath(); ctx.fill(); ctx.shadowColor = 'transparent'; ctx.stroke();
      ctx.fillStyle = '#1f2414'; ctx.textBaseline = 'middle'; ctx.fillText(TEXT, 11, bh / 2 + .5);
      ctx.restore();
    };
    const ease = k => k * k * (3 - 2 * k);
    const rustles = [], t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      let x, y, s, dir, walking = false, point = 0, alpha = 1;
      if (el < OUT) { const k = el / OUT, e = ease(k); x = A.x + (B.x - A.x) * e; y = A.y + (B.y - A.y) * e - Math.sin(k * Math.PI) * 4; s = A.s + (B.s - A.s) * e; dir = 1; walking = true; alpha = Math.min(1, k / .12); if (!rustles.length) rustles.push(now); }
      else if (el < BACK0) { x = B.x; y = B.y; s = 1; dir = el < Q0 - 120 ? 1 : -1; point = el > Q0 && el < Q1 ? 1 : 0; }
      else if (el < BACK1) { const k = (el - BACK0) / (BACK1 - BACK0), e = ease(k); x = B.x + (A.x - B.x) * e; y = B.y + (A.y - B.y) * e - Math.sin(k * Math.PI) * 4; s = B.s + (A.s - B.s) * e; dir = -1; walking = true; alpha = k > .8 ? 1 - (k - .8) / .2 : 1; }
      else if (rustles.length < 2) rustles.push(now);
      if (x !== undefined && alpha > 0) {
        ctx.save(); ctx.globalAlpha = alpha; ctx.translate(x, y); ctx.scale(dir * s, s);
        person(el / 1000 * 2.2 * 6.283, walking, point); ctx.restore();
      }
      if (el > Q0 && el < Q1 + 200) {
        const a = el < Q0 + 180 ? (el - Q0) / 180 : el > Q1 ? 1 - (el - Q1) / 200 : 1;
        const k = el < Q0 + 180 ? .85 + .15 * ease((el - Q0) / 180) : 1;
        bubble(B.x, B.y, Math.max(0, a), k);
      }
      let rot = 0, sy = 1;
      for (const r0 of rustles) { const d = (now - r0) / 1000; if (d < .7) { const e = Math.exp(-d * 6); rot += Math.sin(d * 34) * .7 * e; sy += Math.sin(d * 28) * .01 * e; } }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (el < BACK1 || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(frame);
  }
  otters() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const spd = (this.props.speed ?? 1) * 0.5625, R = (a, b) => a + Math.random() * (b - a);
    const ex = L.x + .76 * L.w, ey = L.y + .93 * L.h;
    const cr = (p0, p1, p2, p3, t) => { const t2 = t * t, t3 = t2 * t; return .5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3); };
    const by = H - 18;
    const way = [
      [W + 120, by - 10], [W + 40, by - 8],
      [R(1020, 1160), by - R(0, 20)],
      [R(760, 880), by - R(70, 110)],
      [R(960, 1080), R(95, 125)],
      [R(620, 720), by - R(0, 15)],
      [R(400, 480), by - R(60, 90)],
      [L.x + L.w + R(90, 150), R(110, 135)],
      [ex + 26, ey - 18], [ex, ey], [ex - 10, ey + 6]
    ];
    const samp = [[way[1][0], way[1][1], 0]];
    for (let k = 0; k < way.length - 3; k++) for (let j = 1; j <= 60; j++) {
      const t = j / 60, a = way[k], b = way[k + 1], c = way[k + 2], d = way[k + 3];
      const x = cr(a[0], b[0], c[0], d[0], t), y = cr(a[1], b[1], c[1], d[1], t), q = samp[samp.length - 1];
      samp.push([x, y, q[2] + Math.hypot(x - q[0], y - q[1])]);
    }
    const LEN = samp[samp.length - 1][2];
    const pos = (d) => {
      d = Math.max(0, Math.min(LEN, d)); let lo = 0, hi = samp.length - 1;
      while (hi - lo > 1) { const m = (lo + hi) >> 1; samp[m][2] < d ? lo = m : hi = m; }
      const a = samp[lo], b = samp[hi], f = (d - a[2]) / (b[2] - a[2] || 1);
      return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
    };
    const n = Math.max(2, Math.min(8, Math.round((this.props.birdCount ?? 7) * .75)));
    const V = .62, STRIDE = 15, DIVE = 46;
    const crew = Array.from({ length: n }, (_, i) => ({ delay: i * R(230, 300), s: R(.95, 1.2) * (i === n - 1 && n > 3 ? .78 : 1), ph: R(0, 6.28), dy: R(-3, 3) }));
    const leg = (x, y, a, l, w, col, paw) => {
      const fx = x + Math.sin(a) * l, fy = y + Math.cos(a) * l;
      ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(fx, fy); ctx.stroke();
      ctx.fillStyle = paw; ctx.beginPath(); ctx.ellipse(fx + .5, fy, 1.2, .7, 0, 0, 6.29); ctx.fill();
    };
    const otter = (p, leap, sway) => {
      const c = '#5a4230', lt = '#c4a67f', dk = '#2e2219';
      const g = leap ? -.6 : Math.sin(p), arch = leap ? .1 : (g + 1) / 2;
      ctx.translate(0, leap ? 0 : -Math.max(0, Math.sin(p + .7)) * 1.5);
      const fA = leap ? -1.25 : -g * .85 + .15, rA = leap ? -1.35 : g * .85 - .15;
      leg(3.8, -3.4, fA + .3, 3.5, 1.9, '#3f2e21', dk);
      leg(-5.2, -3.4, rA - .3, 3.6, 2.1, '#3f2e21', dk);
      ctx.fillStyle = c; ctx.beginPath();
      ctx.moveTo(-6.4, -5.6);
      ctx.quadraticCurveTo(-11.5, -5 + arch * 1.2 + sway * .5, -17, -1.8 - arch * 2.2 + sway);
      ctx.quadraticCurveTo(-11.5, -1.8 + sway * .4, -6.4, -2.6);
      ctx.closePath(); ctx.fill();
      const backY = -8.4 - arch * 2.4, bellyY = -2.1 - arch * 1.3;
      ctx.beginPath(); ctx.moveTo(-6.8, -5.6);
      ctx.bezierCurveTo(-4.4, backY, 2.6, backY + .5, 6.4, -7.4);
      ctx.lineTo(7, -4.2);
      ctx.bezierCurveTo(3, bellyY, -3, bellyY + .4, -6.8, -2.4);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = lt; ctx.beginPath(); ctx.ellipse(5.6, -4.7, 2.1, 1.3, -.25, 0, 6.29); ctx.fill();
      ctx.fillStyle = c; ctx.beginPath(); ctx.ellipse(8.8, -7.2, 3, 2.35, -.08, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(7.2, -9.3, .95, 0, 6.29); ctx.fill();
      ctx.fillStyle = dk; ctx.beginPath(); ctx.arc(7.2, -9.3, .45, 0, 6.29); ctx.fill();
      ctx.fillStyle = lt; ctx.beginPath(); ctx.ellipse(11.1, -6.4, 1.9, 1.4, .1, 0, 6.29); ctx.fill();
      ctx.fillStyle = dk; ctx.beginPath(); ctx.ellipse(12.8, -6.9, .7, .55, 0, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(9.9, -8.1, .5, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(10.05, -8.25, .17, 0, 6.29); ctx.fill();
      ctx.strokeStyle = 'rgba(46,34,25,.65)'; ctx.lineWidth = .3; ctx.beginPath();
      ctx.moveTo(11.8, -6.3); ctx.lineTo(14.6, -7); ctx.moveTo(11.8, -6); ctx.lineTo(14.5, -5.6); ctx.moveTo(11.6, -5.8); ctx.lineTo(13.9, -4.6); ctx.stroke();
      leg(4.8, -3.6, fA - .2, 3.7, 2.1, c, dk);
      leg(-4.2, -3.6, rA + .25, 3.8, 2.4, c, dk);
    };
    const splashes = [], rustles = [], t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const o of crew) {
        const lt = el - o.delay; if (lt < 0) { alive = true; continue; }
        const d = lt * V; if (d >= LEN) {
          if (!o.done) { o.done = true; splashes.push({ t: now, x: ex + R(-3, 3) }); rustles.push(now); }
          continue;
        }
        alive = true;
        const [x, y] = pos(d), [x2, y2] = pos(d + 3);
        const rem = LEN - d, leap = rem < DIVE, k = leap ? 1 - rem / DIVE : 0;
        if (Math.abs(x2 - x) > .05) o.dir = x2 < x ? -1 : 1;
        const pitch = Math.max(-.5, Math.min(.6, Math.atan2(y2 - y, Math.abs(x2 - x) + 1e-6)));
        const s = o.s * (1 - .6 * Math.min(1, Math.max(0, (L.x + L.w + 160 - x) / 200)) * (x < L.x + L.w + 160 ? 1 : 0) - (leap ? .1 * k : 0));
        const lift = leap ? -Math.sin(k * Math.PI) * 9 * s : 0;
        ctx.save(); ctx.globalAlpha = leap && k > .8 ? 1 - (k - .8) / .2 : 1;
        ctx.translate(x, y + o.dy * (1 - k) + lift); ctx.scale((o.dir || -1) * s, s); ctx.rotate(leap ? pitch + .5 * k : pitch);
        otter(d / STRIDE * 6.283 + o.ph, leap, Math.sin(lt / 140 + o.ph) * 1.2);
        ctx.restore();
      }
      for (const sp of splashes) {
        const a = (now - sp.t) / 700; if (a >= 1) continue; alive = true;
        ctx.strokeStyle = `rgba(232,244,248,${(1 - a).toFixed(3)})`; ctx.lineWidth = 1.1;
        for (const r0 of [0, .35]) { const r = Math.max(0, a - r0) * 9; if (r <= 0) continue; ctx.beginPath(); ctx.ellipse(sp.x, ey, r, r * .32, 0, 0, 6.29); ctx.stroke(); }
        ctx.fillStyle = `rgba(232,244,248,${(1 - a).toFixed(3)})`;
        for (let j = 0; j < 4; j++) { const ang = -Math.PI / 2 + (j - 1.5) * .5, h = Math.sin(Math.min(1, a * 1.6) * Math.PI) * 6; ctx.beginPath(); ctx.arc(sp.x + Math.cos(ang) * a * 7, ey - h * (1 - Math.abs(j - 1.5) * .3), .7, 0, 6.29); ctx.fill(); }
      }
      let rot = 0, sy = 1;
      for (const r0 of rustles) { const dd = (now - r0) / 1000; if (dd < .7) { const e = Math.exp(-dd * 6); rot += Math.sin(dd * 34) * .7 * e; sy += Math.sin(dd * 28) * .01 * e; } }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (alive || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(frame);
  }
  night() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const spd = (this.props.speed ?? 1) * 0.5625, G = this.groundY, R = (a, b) => a + Math.random() * (b - a);
    const cr = (p0, p1, p2, p3, t) => { const t2 = t * t, t3 = t2 * t; return .5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3); };
    const at = (pts, u) => { const segs = pts.length - 3, f = Math.min(u * segs, segs - 1e-6), k = Math.floor(f), t = f - k; const a = pts[k], b = pts[k + 1], c = pts[k + 2], d = pts[k + 3]; return [cr(a[0], b[0], c[0], d[0], t), cr(a[1], b[1], c[1], d[1], t)]; };
    const perches = [[.3,.26],[.5,.14],[.72,.3],[.18,.42],[.6,.2],[.86,.42],[.4,.2],[.24,.36]];
    const nb = Math.max(3, Math.min(8, Math.round((this.props.birdCount ?? 7) * .85)));
    const bats = [];
    for (let i = 0; i < nb; i++) {
      const p = perches[i % perches.length], sx = L.x + p[0] * L.w, sy = L.y + p[1] * L.h;
      const out = i % 2 ? [R(500, 1300), -30] : [W + 40, R(10, 120)];
      const pts = [[sx, sy + 10], [sx, sy], [sx + R(-20, 60), sy - R(20, 40)], [R(250, 1000), R(15, 150)], out, [out[0] + 60, out[1] - 30]];
      bats.push({ pts, delay: 800 + i * R(140, 260), dur: R(2300, 3000), ph: R(0, 6.28), hz: R(7, 9), wob: R(2, 5) });
    }
    const coon = { delay: 1300, sx: L.x + .5 * L.w, sy: L.y + .8 * L.h, gx: L.x + .32 * L.w, hop: 380, run: 1050 };
    const bat = (f) => {
      const c = '#15131c', tip = -f * 7, mid = -f * 3;
      ctx.fillStyle = c;
      for (const s of [-1, 1]) {
        ctx.beginPath(); ctx.moveTo(0, -1.5);
        ctx.quadraticCurveTo(s * 5, mid - 3, s * 11, tip - 1);
        ctx.quadraticCurveTo(s * 9.5, tip * .5 + 1.5, s * 8, tip * .4 + 2.6);
        ctx.quadraticCurveTo(s * 6.5, mid * .6 + .8, s * 5, mid * .5 + 2.6);
        ctx.quadraticCurveTo(s * 3.5, 1, s * 2.2, 2.4);
        ctx.quadraticCurveTo(s * 1, 1.2, 0, 2); ctx.closePath(); ctx.fill();
      }
      ctx.beginPath(); ctx.ellipse(0, .4, 1.5, 2.4, 0, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(0, -2, 1.3, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.moveTo(-1.2, -2.4); ctx.lineTo(-1, -4.4); ctx.lineTo(-.2, -2.9); ctx.moveTo(1.2, -2.4); ctx.lineTo(1, -4.4); ctx.lineTo(.2, -2.9); ctx.fill();
    };
    const leg = (x, y, a, l, w, col) => { ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.sin(a) * l, y + Math.cos(a) * l); ctx.stroke(); };
    const raccoon = (p, leap) => {
      const c = '#77736b', dk = '#26231f', bob = leap ? -1 : -Math.abs(Math.sin(p)) * 1.6;
      ctx.translate(0, bob);
      const fA = leap ? .9 : Math.sin(p) * .7, rA = leap ? -.9 : Math.sin(p + 2.8) * .7;
      leg(4, -5, fA + .2, 5, 1.6, dk); leg(-4.5, -5, rA - .2, 5, 1.6, dk);
      ctx.save(); ctx.translate(-6.5, -7); ctx.rotate(-.35 + (leap ? .15 : Math.sin(p) * .08));
      for (let i = 0; i < 5; i++) { ctx.fillStyle = i % 2 ? dk : c; ctx.beginPath(); ctx.ellipse(-1.4 - i * 1.6, 0, 1.2, 2.1 - i * .12, 0, 0, 6.29); ctx.fill(); }
      ctx.restore();
      ctx.fillStyle = c; ctx.beginPath(); ctx.ellipse(0, -7.5, 6.8, 3.6, -.08, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(6.6, -9, 2.8, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.moveTo(8.4, -10); ctx.lineTo(11.6, -8.6); ctx.lineTo(8.6, -7.4); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(4.8, -11); ctx.lineTo(5.2, -13.4); ctx.lineTo(6.8, -11.6); ctx.closePath(); ctx.moveTo(6.8, -11.6); ctx.lineTo(7.6, -13.6); ctx.lineTo(8.4, -11.2); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#f0ede4'; ctx.beginPath(); ctx.ellipse(8.2, -8.2, 2.4, 1.3, .2, 0, 6.29); ctx.fill();
      ctx.fillStyle = dk; ctx.beginPath(); ctx.ellipse(7.4, -9.6, 2.6, .9, .1, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(11.5, -8.6, .65, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#f0ede4'; ctx.beginPath(); ctx.arc(7.9, -9.7, .45, 0, 6.29); ctx.fill();
      leg(5, -5, fA - .3, 5, 1.7, dk); leg(-3.5, -5, rA + .3, 5, 1.7, dk);
    };
    const DIM_IN = 800, DIM_OUT_AT = 4300, DIM_OUT = 900;
    const rustles = [], t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      const ease = k => k * k * (3 - 2 * k);
      const dim = el < DIM_IN ? ease(el / DIM_IN) : el < DIM_OUT_AT ? 1 : Math.max(0, 1 - ease(Math.min(1, (el - DIM_OUT_AT) / DIM_OUT)));
      if (dim > 0) { ctx.fillStyle = `rgba(14,20,44,${(.46 * dim).toFixed(3)})`; ctx.fillRect(0, 0, W, H); }
      for (const b of bats) {
        const lt = el - b.delay; if (lt < 0) continue;
        const t = lt / b.dur; if (t >= 1) continue;
        if (!b.out) { b.out = true; rustles.push(now); }
        let [x, y] = at(b.pts, t < .2 ? t * .8 : .16 + (t - .2) * 1.05);
        x += Math.sin(lt / 130 + b.ph) * b.wob; y += Math.sin(lt / 85 + b.ph * 2) * b.wob * .7;
        const s = .35 + .65 * Math.min(1, t / .3);
        ctx.save(); ctx.globalAlpha = Math.min(1, t / .08); ctx.translate(x, y); ctx.rotate(Math.sin(lt / 160 + b.ph) * .25); ctx.scale(s, s);
        bat(Math.sin(lt / 1000 * b.hz * 6.283 + b.ph)); ctx.restore();
      }
      const lt = el - coon.delay;
      if (lt >= 0 && lt < coon.hop + coon.run) {
        if (!coon.out) { coon.out = true; rustles.push(now); }
        let x, y, s, leap = false, rot = 0;
        if (lt < coon.hop) { const k = lt / coon.hop; leap = true; x = coon.sx + (coon.gx - coon.sx) * k; y = coon.sy + (G - coon.sy) * k - Math.sin(k * Math.PI) * 10; s = .4 + .6 * k; rot = .3 * Math.cos(k * Math.PI); }
        else { const k = (lt - coon.hop) / coon.run; x = coon.gx + (-40 - coon.gx) * k; y = G; s = 1; }
        ctx.save(); ctx.globalAlpha = Math.min(1, lt / 120); ctx.translate(x, y); ctx.scale(-s, s); ctx.rotate(rot);
        raccoon(lt / 1000 * 4.4 * 6.283, leap); ctx.restore();
      }
      let rot = 0, sy = 1;
      for (const r0 of rustles) { const d = (now - r0) / 1000; if (d < .7) { const e = Math.exp(-d * 6); rot += Math.sin(d * 34) * .8 * e; sy += Math.sin(d * 28) * .012 * e; } }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (el < DIM_OUT_AT + DIM_OUT || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(frame);
  }
  animals() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const spd = (this.props.speed ?? 1) * 0.5625, G = this.groundY;
    const leg = (x, y, a1, a2, l1, l2, w, col) => {
      const kx = x + Math.sin(a1) * l1, ky = y + Math.cos(a1) * l1;
      ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(kx, ky); ctx.lineTo(kx + Math.sin(a1 + a2) * l2, ky + Math.cos(a1 + a2) * l2); ctx.stroke();
    };
    const deer = (p, leap) => {
      const c = '#5a4630', bob = leap ? -2 : -Math.abs(Math.sin(p)) * 3;
      ctx.translate(0, bob);
      const fA = leap ? 1.0 : Math.sin(p) * .75, rA = leap ? -1.0 : Math.sin(p + 2.4) * .75;
      leg(4.5, -11, fA + .15, leap ? -.2 : Math.max(0, -Math.cos(p)) * 1.1, 5.5, 6, 1.4, '#3f3122');
      leg(-6, -11, rA - .1, leap ? .2 : -Math.max(0, Math.cos(p + 2.4)) * 1.1, 5.5, 6, 1.4, '#3f3122');
      ctx.fillStyle = c;
      ctx.beginPath(); ctx.ellipse(0, -13, 8, 4, -.05, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.moveTo(4, -16); ctx.lineTo(8.5, -23.5); ctx.lineTo(11, -22.5); ctx.lineTo(8, -12); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.ellipse(11.5, -23, 3.2, 1.8, .35, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.moveTo(9, -24.5); ctx.lineTo(8.2, -27.5); ctx.lineTo(10.4, -25); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#3f3122'; ctx.lineWidth = .9; ctx.beginPath();
      ctx.moveTo(10.2, -24.6); ctx.lineTo(9.6, -30); ctx.moveTo(9.9, -27.5); ctx.lineTo(12, -29.5); ctx.moveTo(9.7, -29); ctx.lineTo(8, -31); ctx.stroke();
      ctx.fillStyle = '#f7f6f1'; ctx.beginPath(); ctx.ellipse(-8.2, -14.5, 1.6, 1.1, -.5, 0, 6.29); ctx.fill();
      leg(5.5, -11, fA - .25, leap ? -.2 : Math.max(0, -Math.cos(p + .4)) * 1.1, 5.5, 6, 1.5, c);
      leg(-5, -11, rA + .2, leap ? .2 : -Math.max(0, Math.cos(p + 2.8)) * 1.1, 5.5, 6, 1.5, c);
    };
    const fox = (p, leap) => {
      const c = '#b5541f', bob = leap ? -1 : -Math.abs(Math.sin(p)) * 1.8;
      ctx.translate(0, bob);
      const fA = leap ? 1.1 : Math.sin(p) * .8, rA = leap ? -1.1 : Math.sin(p + 2.6) * .8;
      leg(4.5, -6, fA + .2, 0, 3.2, 3.2, 1.3, '#3a2a1e');
      leg(-4.5, -6, rA - .2, 0, 3.2, 3.2, 1.3, '#3a2a1e');
      ctx.fillStyle = c;
      ctx.save(); ctx.translate(-6, -7.5); ctx.rotate(-.18 + (leap ? .1 : Math.sin(p) * .06));
      ctx.beginPath(); ctx.ellipse(-5.5, 0, 6.5, 2.4, 0, 0, 6.29); ctx.fill();
      ctx.fillStyle = '#f7f6f1'; ctx.beginPath(); ctx.ellipse(-11, 0, 1.8, 1.6, 0, 0, 6.29); ctx.fill();
      ctx.restore(); ctx.fillStyle = c;
      ctx.beginPath(); ctx.ellipse(0, -7.5, 6.5, 2.7, 0, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.arc(7, -9, 2.4, 0, 6.29); ctx.fill();
      ctx.beginPath(); ctx.moveTo(8, -10.2); ctx.lineTo(11.8, -8.6); ctx.lineTo(8.4, -7.6); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(5.6, -10.4); ctx.lineTo(6.2, -13.4); ctx.lineTo(7.6, -10.8); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(7.4, -10.6); ctx.lineTo(8.4, -13.2); ctx.lineTo(9.2, -10.2); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#3a2a1e'; ctx.beginPath(); ctx.arc(11.7, -8.6, .6, 0, 6.29); ctx.fill();
      leg(5, -6, fA - .3, 0, 3.2, 3.2, 1.4, '#3a2a1e');
      leg(-4, -6, rA + .3, 0, 3.2, 3.2, 1.4, '#3a2a1e');
    };
    const crew = [
      { draw: fox, delay: 0, dur: 2500, hz: 4.6, sx: W + 30, ex: L.x + .3 * L.w, s: 1.05 },
      { draw: deer, delay: 380, dur: 2800, hz: 3.0, sx: W + 40, ex: L.x + .6 * L.w, s: 1 }
    ];
    if ((this.props.birdCount ?? 7) > 8) crew.push({ draw: fox, delay: 900, dur: 2700, hz: 4.8, sx: W + 30, ex: L.x + .82 * L.w, s: .85 });
    const endY = L.y + .8 * L.h;
    const rustles = [], t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const a of crew) {
        const lt = el - a.delay;
        if (lt < 0) { alive = true; continue; }
        const t = Math.min(1, lt / a.dur);
        let x = a.sx + (a.ex - a.sx) * t, y = G, s = a.s, alpha = 1, leap = false;
        if (t > .84) {
          const k = (t - .84) / .16; leap = true;
          y = G + (endY - G) * k - Math.sin(k * Math.PI) * 12; s = a.s * (1 - .6 * k);
        }
        if (t >= 1) {
          if (!a.landed) { a.landed = true; rustles.push(now); }
          const after = lt - a.dur; y = endY + Math.min(2, after / 150); s = a.s * .4;
          alpha = Math.max(0, 1 - after / 350);
        }
        if (alpha <= 0) continue; alive = true;
        ctx.save(); ctx.globalAlpha = alpha; ctx.translate(x, y); ctx.scale(-s, s);
        if (leap) ctx.rotate(-.25 * Math.cos(((t - .84) / .16) * Math.PI));
        a.draw(lt / 1000 * a.hz * 6.283, leap);
        ctx.restore();
      }
      let rot = 0, sy = 1;
      for (const r0 of rustles) {
        const d = (now - r0) / 1000;
        if (d < .7) { const e = Math.exp(-d * 6); rot += Math.sin(d * 34) * 1.1 * e; sy += Math.sin(d * 28) * .015 * e; }
      }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (alive || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(frame);
  }
  birds() {
    cancelAnimationFrame(this.raf);
    const cv = this.canvasRef.current, fr = this.frameRef.current, logo = this.logoRef.current;
    if (!cv || !fr || !logo) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches && this.respectReducedMotion) return;
    const W = fr.offsetWidth, H = fr.offsetHeight, dpr = Math.max(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    const ctx = cv.getContext('2d');
    const frect = fr.getBoundingClientRect(), sc = frect.width / fr.offsetWidth;
    const lr = logo.getBoundingClientRect();
    const L = { x: (lr.left - frect.left) / sc, y: (lr.top - frect.top) / sc, w: lr.width / sc, h: lr.height / sc };
    const perches = [[.14,.42],[.30,.24],[.47,.10],[.57,.20],[.72,.27],[.86,.40],[.24,.34],[.40,.16],[.64,.24],[.80,.36],[.52,.14],[.18,.40],[.34,.22],[.68,.26]];
    const n = Math.max(1, Math.min(14, this.props.birdCount ?? 7));
    const spd = (this.props.speed ?? 1) * 0.5625;
    const R = (a, b) => a + Math.random() * (b - a);
    const birds = [];
    for (let i = 0; i < n; i++) {
      const p = perches[i % perches.length];
      const ex = L.x + p[0] * L.w, ey = L.y + p[1] * L.h;
      const fromLeft = i % 3 === 1;
      const pts = [
        [fromLeft ? -40 : W + R(30, 120), R(20, 170)],
        [R(500, 1250), R(18, 175)],
        [R(250, 900), R(15, 160)],
        [ex + R(40, 200), ey - R(35, 70)],
        [ex + R(6, 18), ey - R(8, 14)],
        [ex, ey]
      ];
      pts.unshift([pts[0][0] + (fromLeft ? -60 : 60), pts[0][1]]);
      pts.push([ex - 4, ey + 2]);
      birds.push({ pts, ex, ey, delay: R(0, 1100) + i * 70, dur: R(2600, 3400), ph: R(0, 6.28), fr: R(15, 19), size: R(.85, 1.15), landed: false });
    }
    const cr = (p0, p1, p2, p3, t) => {
      const t2 = t * t, t3 = t2 * t;
      return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
    };
    const at = (pts, u) => {
      const segs = pts.length - 3, f = Math.min(u * segs, segs - 1e-6), k = Math.floor(f), t = f - k;
      const a = pts[k], b = pts[k + 1], c = pts[k + 2], d = pts[k + 3];
      return [cr(a[0], b[0], c[0], d[0], t), cr(a[1], b[1], c[1], d[1], t)];
    };
    const rustles = [];
    const t0 = performance.now();
    const draw = (now) => {
      const el = (now - t0) * spd;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const b of birds) {
        const lt = el - b.delay;
        if (lt < 0) { alive = true; continue; }
        const t = Math.min(1, lt / b.dur);
        const u = t < .6 ? t * 1.05 : .63 + (1 - Math.pow(1 - (t - .6) / .4, 2.2)) * .37;
        let [x, y] = at(b.pts, Math.min(u, 1));
        const [x2, y2] = at(b.pts, Math.min(u + .01, 1));
        let flap, span = 1, alpha = 1, s = b.size * (1 - .45 * Math.max(0, (t - .55) / .45));
        if (t < .78) {
          const glide = Math.sin(lt / 420 + b.ph) > .55;
          flap = glide ? .15 + .08 * Math.sin(lt / 90) : Math.sin(lt / 1000 * b.fr + b.ph);
        } else if (t < 1) {
          const k = (t - .78) / .22;
          flap = .9 - k * .3 + .12 * Math.sin(lt / 40); span = 1 - k * .55;
        } else {
          if (!b.landed) { b.landed = true; rustles.push(now); }
          const after = lt - b.dur;
          flap = .6; span = Math.max(.15, .45 - after / 500);
          y += Math.min(4, after / 120);
          alpha = Math.max(0, 1 - Math.max(0, after - 250) / 350);
        }
        if (alpha > 0) alive = true; else continue;
        const vx = x2 - x, vy = y2 - y;
        if (Math.abs(vx) > .05) b.dir = vx < 0 ? -1 : 1;
        const dir = b.dir || -1;
        const pitch = Math.max(-.55, Math.min(.55, Math.atan2(vy, Math.abs(vx) + 1e-6)));
        ctx.save(); ctx.globalAlpha = alpha; ctx.translate(x, y); ctx.scale(dir * s, s); ctx.rotate(pitch);
        const wing = (k, col) => {
          const tipY = -flap * 9 * span * k, tipX = -3.5 - 3 * span;
          ctx.fillStyle = col; ctx.beginPath();
          ctx.moveTo(2, -.6);
          ctx.quadraticCurveTo(1, tipY * .7 - 1.2 * Math.sign(flap || 1), tipX, tipY);
          ctx.quadraticCurveTo(-3.2, tipY * .35, -2.2, -.3);
          ctx.closePath(); ctx.fill();
        };
        wing(.72, '#4a5236');
        ctx.fillStyle = '#1f2414';
        ctx.beginPath(); ctx.ellipse(0, 0, 4.6, 1.7, 0, 0, 6.29); ctx.fill();
        ctx.beginPath(); ctx.arc(4.6, -.7, 1.6, 0, 6.29); ctx.fill();
        ctx.beginPath(); ctx.moveTo(5.9, -1.1); ctx.lineTo(7.8, -.5); ctx.lineTo(5.9, 0); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.moveTo(-3.6, -.8); ctx.lineTo(-8.2, -2); ctx.lineTo(-6.9, -.1); ctx.lineTo(-8.2, 1.6); ctx.lineTo(-3.6, .9); ctx.closePath(); ctx.fill();
        wing(1, '#1f2414');
        ctx.restore();
      }
      let rot = 0, sy = 1;
      for (const r0 of rustles) {
        const d = (now - r0) / 1000;
        if (d < .7) { const e = Math.exp(-d * 6); rot += Math.sin(d * 34) * .9 * e; sy += Math.sin(d * 28) * .012 * e; }
      }
      logo.style.transform = rot || sy !== 1 ? `rotate(${rot.toFixed(3)}deg) scaleY(${sy.toFixed(4)})` : '';
      if (alive || rustles.some(r0 => now - r0 < 700)) this.raf = requestAnimationFrame(draw);
      else { ctx.clearRect(0, 0, W, H); logo.style.transform = ''; }
    };
    this.raf = requestAnimationFrame(draw);
  }
}
